import { Logger } from '../logger';
const console = new Logger('AutoQa');
export const QA_CORPUS_URL = '/data/qa-corpus.json.gz';
export const QA_CORPUS_READY_EVENT = 'counterpunch:qa-corpus-ready';
/**
 * Loads the bundled Auto QA identity table once. Matching stays in-process;
 * this is counts JSON, not a neural net.
 */
export class QaCorpusIndex {
    constructor() {
        this.table = null;
        this.markAnchorNames = new Set();
        this.readyPromise = null;
    }
    isReady() {
        return this.table !== null;
    }
    getTable() {
        return this.table;
    }
    isMarkAnchorName(name) {
        return this.markAnchorNames.has(name);
    }
    async ensureReady() {
        if (this.isReady()) {
            return;
        }
        if (!this.readyPromise) {
            this.readyPromise = this.load().catch((error) => {
                this.readyPromise = null;
                throw error;
            });
        }
        return this.readyPromise;
    }
    getIdentity(identity) {
        return this.table?.identities[identity];
    }
    /** Test helper: replace the table with an in-memory corpus. */
    loadTableForTests(table) {
        this.applyTable(table);
        this.readyPromise = Promise.resolve();
    }
    /** Test helper: clear so ensureReady() reloads. */
    resetForTests() {
        this.table = null;
        this.markAnchorNames = new Set();
        this.readyPromise = null;
    }
    async load() {
        const response = await fetch(QA_CORPUS_URL);
        if (!response.ok) {
            throw new Error(`Unable to load Auto QA corpus (${response.status}).`);
        }
        const table = JSON.parse(await decompressGzipResponse(response));
        this.applyTable(table);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(QA_CORPUS_READY_EVENT));
        }
        console.log(`Loaded Auto QA corpus (${Object.keys(table.identities || {}).length} identities).`);
    }
    applyTable(table) {
        this.table = {
            version: table.version || 1,
            mark_anchor_names: Array.isArray(table.mark_anchor_names)
                ? table.mark_anchor_names
                : [],
            identities: table.identities || {}
        };
        this.markAnchorNames = new Set(this.table.mark_anchor_names);
    }
}
async function decompressGzipResponse(response) {
    if (typeof DecompressionStream === 'undefined') {
        throw new Error('Gzip DecompressionStream is not available.');
    }
    if (!response.body) {
        throw new Error('Auto QA corpus response has no body.');
    }
    const stream = response.body.pipeThrough(new DecompressionStream('gzip'));
    return new Response(stream).text();
}
export const qaCorpusIndex = new QaCorpusIndex();
globalThis.qaCorpusIndex = qaCorpusIndex;
if (typeof window !== 'undefined' && typeof fetch === 'function') {
    void qaCorpusIndex
        .ensureReady()
        .catch((error) => console.warn('Auto QA corpus will be retried when opened.', error));
}
